import { ApiClient } from '../ApiClient';

export class LPBondsCollectionsApi {
  /**
   * Constructs a new LPBondsCollectionsApi.
   * @alias module:api/LPBondsCollectionsApi
   * @class
   * @param {module:ApiClient} [apiClient] Optional API client implementation to use,
   * default to {@link module:ApiClient#lpBondsApiInstance} if unspecified.
   */
  constructor(apiClient) {
    this.apiClient = apiClient || ApiClient.lpBondsApiInstance;
  }

  /**
   * List details of a specific collection
   * @param {String} contractAddress Contract address of the collection
   */
  getCollectionFromAddress({ address }) {
    return this.apiClient.callApi('/collections/{address}', 'get', { address }, {}, null);
  }

  /**
   * Get LP Bond collection by bond key/slug
   * Resolves URL slug (e.g., 'APE-GMI-L3') to collection data
   * @param {String} bondKey Bond key/slug from URL (e.g., 'APE-GMI-L3', 'ETH-GMI')
   * @returns {Promise} Collection data with bondKey and resolvedChain
   */
  getCollectionByBondKey({ bondKey }) {
    return this.apiClient.callApi('/collections/lpbond/{bondKey}', 'get', { bondKey }, {}, null);
  }

  /**
   * Get lightweight bond routes for navigation
   * Returns only address, route, and chainId - no database queries
   * @returns {Promise<{data: Array<{address: string, route: string, chainId: number}>}>}
   */
  getBondRoutes() {
    return this.apiClient.callApi('/collections/bond-routes', 'get', {}, {}, null);
  }

  getPortfolio(ownerAddress) {
    return this.apiClient.callApi(
      '/collections/portfolio/{ownerAddress}',
      'get',
      { ownerAddress },
      {},
      null,
    );
  }

  getCollectionBidsFromAddress({ address }) {
    return this.apiClient.callApi('/collections/bids/{address}', 'get', { address }, {}, null);
  }

  getLaunchpadCollection(collectionUrl) {
    return this.apiClient.callApi(
      '/collections/launchpad/{collectionUrl}',
      'get',
      { collectionUrl },
      {},
      null,
    );
  }

  getCollectionHolders(address) {
    return this.apiClient.callApi('/collections/holders/{address}', 'get', { address }, {}, null);
  }

  /**
   * Get collection trading history
   *
   * @param {string} contractAddress Contract address of the collection
   * @param {boolean} listing Include listing events
   * @param {boolean} mint Include mint events
   * @param {boolean} sale Include sale events
   * @param {boolean} offer Include offer events
   * @param {boolean} bid Include bid events
   * @param {boolean} burn Include burn events
   * @param {boolean} transfer Include transfer events
   * @param {Number} skip Number of items to skip
   * @param {Number} limit Number of items to retrieve
   * @param {string} date Date filter
   */
  getCollectionTradingHistory({
    contractAddress,
    listing,
    mint,
    sale,
    offer,
    bid,
    burn,
    transfer,
    skip,
    limit,
    date,
  }) {
    return this.apiClient.callApi(
      '/collections/tradingHistory/{contractAddress}',
      'get',
      { contractAddress },
      { listing, mint, sale, offer, bid, burn, transfer, skip, limit, date },
      null,
    );
  }

  /**
   * Update NftContract metaStatus
   * @param {string} contractAddress Contract address of the NFT
   * @param {string} metaStatus New meta status value
   */
  async updateMetaStatus(contractAddress, metaStatus) {
    return await this.apiClient.callApi(
      '/collections/update-meta-status',
      'post',
      {},
      {},
      null,
      {},
      { contractAddress, metaStatus },
    );
  }

  /**
   * Get favorite collections for a user
   * @param {String} address User wallet address
   */
  getFavoriteCollections(address) {
    return this.apiClient.callApi('/collections/favorite/{address}', 'get', { address }, {}, null);
  }

  /**
   * Cancel a collection bid
   * @param {String} orderKey The order key of the bid to cancel
   */
  cancelCollectionBid(orderKey) {
    return this.apiClient.callApi(
      '/collections/cancelBid/{orderKey}',
      'post',
      { orderKey },
      {},
      null,
    );
  }
}
